import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  QueryList,
  signal,
  Signal,
  ViewChildren,
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { PrimeIcons } from 'primeng/api';
import { Button } from 'primeng/button';
import { CardModule } from 'primeng/card';
import {
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  shareReplay,
  startWith,
  switchMap,
} from 'rxjs';

import { ConfigService, CurrentSiteState } from '@ppg/configuration';
import { FeatureModule } from '@ppg/core/content';
import { ClientSiteSettingsKeys, MixedContentPageType, ResourceItem, TextAlignment } from '@ppg/core/enums';
import { LanguagesService } from '@ppg/core/language';
import {
  Asset,
  Breadcrumb,
  ColorDetailsPageContent,
  ColorInfoValid,
  Component603010Model,
  Container603010Model,
  Content,
  CTA,
  PageLevelMeta,
  StaticAsset,
} from '@ppg/core/models';
import { AppRoutesService, FeatureRoutePathService } from '@ppg/core/navigation';
import { GoogleAnalyticsCustomerJourney, GoogleAnalyticsPagePrefix, GoogleAnalyticsEvent } from '@ppg/shared/analytics';
import { BreadcrumbsComponent } from '@ppg/shared/breadcrumbs';
import {
  COLOR_EMG_QUERY_PARAM,
  ColorGtmService,
  ColorInfoService,
  FavoriteColorLocalStorageService,
  FavoriteColorsService,
} from '@ppg/shared/color/services';
import {
  STRIPE_ID_QUERY_PARAM,
  TintsAndShadesUrlUtilsService,
  TintsAndShadesDataService,
} from '@ppg/shared/color/tints-and-shades';
import {
  ColorWarrantyComponent,
  Container603010Component,
  ImageComponent,
  PageLevelMetaService,
} from '@ppg/shared/components';
import { GalleriaComponent, GalleriaImage, GalleriaType } from '@ppg/shared/galleria';
import { appendImageSize, AssetType } from '@ppg/shared/product-api';
import { ResourceService } from '@ppg/shared/resource';
import { UrlCtaService } from '@ppg/shared/utils';

import { COLOR_ID_QUERY_PARAM, COLOR_PRODUCT_NUMBER_PARAM } from '../../constants/details-page-params.const';
import { ColorData, ColorSpecification } from '../../models/color-specification.model';
import { CoordinatingColors } from '../../models/coordinating-colors.model';
import { CoordinatingColorsService } from '../../services/coordinating-colors.service';
import { ColorDetailsDescriptionComponent } from '../color-details-description/color-details-description';
import { ColorDetailsHeaderBannerComponent } from '../color-details-header-banner/color-details-header-banner.component';
import { ColorSpecificationComponent } from '../color-specification/color-specification.component';

@Component({
  selector: 'ppg-color-details',
  standalone: true,
  imports: [
    Button,
    CardModule,
    ColorDetailsHeaderBannerComponent,
    ColorDetailsDescriptionComponent,
    ColorSpecificationComponent,
    BreadcrumbsComponent,
    ColorWarrantyComponent,
    ImageComponent,
    GalleriaComponent,
    CommonModule,
    Container603010Component,
  ],
  templateUrl: './color-details.component.html',
  styleUrl: './color-details.component.scss',
})
export class ColorDetailsComponent implements AfterViewInit {
  @ViewChildren('headerBannerContainer') headerContainers: QueryList<ElementRef> | undefined = undefined;
  readonly #desktopHeaderHeight = signal('0px');
  readonly desktopHeaderHeight = this.#desktopHeaderHeight.asReadonly();

  readonly #destroyRef = inject(DestroyRef);
  readonly #router = inject(Router);
  readonly #route = inject(ActivatedRoute);
  readonly #colorInfoService = inject(ColorInfoService);
  readonly #currentSiteState = inject(CurrentSiteState);
  readonly #favoriteColorStorageService = inject(FavoriteColorLocalStorageService);
  readonly #favoriteColorsService = inject(FavoriteColorsService);
  readonly #urlCtaService: UrlCtaService = inject(UrlCtaService);
  readonly #resourceService = inject(ResourceService);
  readonly #languagesService = inject(LanguagesService);
  readonly #configService = inject(ConfigService);
  readonly #appRoutesService = inject(AppRoutesService);
  readonly #colorGtmService = inject(ColorGtmService);
  readonly #coordinatingColorsService = inject(CoordinatingColorsService);
  readonly #tintsAndShadesDataService = inject(TintsAndShadesDataService);
  readonly #tintsAndShadesUrlUtilsService = inject(TintsAndShadesUrlUtilsService);
  readonly #metaService = inject(Meta);
  readonly #titleService = inject(Title);
  readonly #pageLevelMetaService = inject(PageLevelMetaService);
  readonly #featureRoutePathService = inject(FeatureRoutePathService);

  readonly storeLocatorFindAndBuyIndex = 0;
  readonly colorConsultationFindAndBuyIndex = 1;

  readonly PrimeIcons = PrimeIcons;
  readonly content = input.required<Content>();
  readonly pageContent = computed(() => this.content().page.content as ColorDetailsPageContent);
  readonly breadcrumbsHome = computed(() => this.content()?.resources[this.breadcrumbHomeCodename]);
  readonly breadcrumbs = computed(() => this.#buildBreadcrumbs(this.colorDetails(), this.content()));
  readonly findAndBuyCtaItems = computed(() => this.pageContent().ctaFindAndBuySupportedPaint);
  readonly ctaShowProductsContent = computed(() => this.pageContent().ctaShowProducts);
  readonly ctaLaunchColorVisualizer = computed(() => this.pageContent().ctaLaunchColorVisualizer);
  readonly colorWarranty = computed(() => this.pageContent().colorWarranty);

  readonly breadcrumbHomeCodename = this.#currentSiteState.getSiteSettingValueByKey(
    ClientSiteSettingsKeys.FeatureBreadcrumbsHome,
  );
  readonly isColorDataSectionEnabled = computed(() =>
    this.#currentSiteState.getSiteFlag(ClientSiteSettingsKeys.ColorJourneyColorDataSectionEnabled),
  );
  readonly isColorDataRGBEnabled = computed(() =>
    this.#currentSiteState.getSiteFlag(ClientSiteSettingsKeys.ColorJourneyColorDataRGBEnabled),
  );
  readonly isColorDataLRVEnabled = computed(() =>
    this.#currentSiteState.getSiteFlag(ClientSiteSettingsKeys.ColorJourneyColorDataLRVEnabled),
  );
  readonly isColorDataNCSEnabled = computed(() =>
    this.#currentSiteState.getSiteFlag(ClientSiteSettingsKeys.ColorJourneyColorDataNCSEnabled),
  );
  readonly tintsAndShadesEnabled = computed(() =>
    this.#currentSiteState.getSiteFlag(ClientSiteSettingsKeys.ColorJourneyTintsAndShadesEnabled),
  );
  readonly cordinatingColorsEnabled = computed(() =>
    this.#currentSiteState.getSiteFlag(ClientSiteSettingsKeys.ColorJourneyCoordinatingColorsEnabled),
  );
  readonly colorToProductJourneyEnabled = computed(() =>
    this.#currentSiteState.getSiteFlag(ClientSiteSettingsKeys.ColorToProductJourneyEnabled),
  );
  readonly videoThumbnailCodename = computed(() =>
    this.#currentSiteState.getSiteSettingValueByKey(ClientSiteSettingsKeys.BrandingAssetsVideoThumbnail),
  );
  readonly algorithmsFromConfig = computed(() => {
    const algorithm1 = this.#currentSiteState.getSiteSettingValueByKey(
      ClientSiteSettingsKeys.ColorJourneyCoordinatingColorsAlgorithm1,
    );
    const algorithm2 = this.#currentSiteState.getSiteSettingValueByKey(
      ClientSiteSettingsKeys.ColorJourneyCoordinatingColorsAlgorithm2,
    );
    const algorithm3 = this.#currentSiteState.getSiteSettingValueByKey(
      ClientSiteSettingsKeys.ColorJourneyCoordinatingColorsAlgorithm3,
    );

    return [algorithm1, algorithm2, algorithm3].filter((algorithm) => !!algorithm);
  });

  readonly #colorProductNumber$ = this.#route.paramMap.pipe(
    map((paramMap) => paramMap.get(COLOR_PRODUCT_NUMBER_PARAM)),
    distinctUntilChanged(),
  );
  readonly #queryParamsColorProductNumber$ = this.#route.queryParams.pipe(
    map((queryParams): string | undefined => queryParams[COLOR_ID_QUERY_PARAM]),
    distinctUntilChanged(),
  );
  readonly #queryParamsStripeId$ = this.#tintsAndShadesUrlUtilsService.getStripeIdFromQueryParams(
    this.#route.queryParams,
  );
  readonly #colorDetails$ = combineLatest({
    paramMapProductNumber: this.#colorProductNumber$,
    queryParamsProductNumber: this.#queryParamsColorProductNumber$,
  }).pipe(
    map(({ paramMapProductNumber, queryParamsProductNumber }) => paramMapProductNumber || queryParamsProductNumber),
    filter(Boolean),
    switchMap((colorProductNumber) => {
      return this.#colorInfoService.getColorDetails(colorProductNumber);
    }),
    shareReplay(1),
  );

  readonly #cordinatingColorsEnabled$ = toObservable(this.cordinatingColorsEnabled);

  readonly colorDetails = toSignal(this.#colorDetails$, { initialValue: null });

  readonly tintsAndShades = toSignal(
    this.#tintsAndShadesDataService
      .getTintsAndShadesByParams({
        colorInfo: this.#colorDetails$,
        stripeId: this.#queryParamsStripeId$,
        tintsAndShadesEnabled: toObservable(this.tintsAndShadesEnabled),
      })
      .pipe(map((stripe) => (stripe?.id ? stripe : null))),
    { initialValue: null },
  );

  readonly coordinatingColors = toSignal(
    combineLatest([this.#colorDetails$, this.#cordinatingColorsEnabled$]).pipe(
      filter(([colorInfo, enabled]) => Boolean(colorInfo) && enabled),
      switchMap(([colorInfo]) => this.#getCoordinatingColors(colorInfo)),
    ),
    {
      initialValue: null,
    },
  );

  readonly ctaShowProducts = computed(() => this.#getCtaShowProducts());

  readonly isFavorite = computed(() => {
    const colorDetails = this.colorDetails();
    const favoriteColors = this.#favoriteColorStorageService.favoriteColors();

    if (!colorDetails) {
      return false;
    }
    return favoriteColors.some((c) => c.productNumber === colorDetails.productNumber);
  });

  readonly resources = computed(() => {
    return {
      findAndBuySectionHeadline: this.#resourceService.getResourceByKey(
        ResourceItem.COLOR_DETAILS_FIND_AND_BUY_SUPPORTED_PAINT_HEADLINE,
      ),
      launchColorVisualizerHeadline: this.#resourceService.getResourceByKey(
        ResourceItem.COLOR_DETAILS_VISUALIZE_THE_COLOR_HEADLINE,
      ),
    };
  });

  readonly videoThumbnail: Signal<StaticAsset | undefined> = this.getVideoThumbnail()
    ? toSignal<StaticAsset | undefined>(this.getVideoThumbnail())
    : signal(undefined);

  readonly isFindAndBuySectionVisible: Signal<boolean> = computed(() => {
    return this.findAndBuyCtaItems().some((x) => x?.content?.url);
  });

  readonly colorSpecification = computed(() => this.mapToColorSpecification(this.colorDetails()));

  readonly areMultipleFamilyTypesEnabled = this.#colorInfoService.areMultipleFamilyTypesEnabled;

  readonly colorFamilyNavigationPath = computed(
    () => this.#appRoutesService.getPathByAppRouteFeatureModule(FeatureModule.ColorFamilies) ?? '',
  );
  readonly colorCollectionNavigationPath = computed(
    () => this.#appRoutesService.getPathByAppRouteFeatureModule(FeatureModule.ColorCollection) ?? '',
  );

  readonly galleriaImages = computed(() => {
    const ambianceItems = this.colorDetails()?.assets.filter((asset) => asset.type.toLowerCase() === 'ambiance');
    const colorImageItems = this.colorDetails()?.assets.filter((asset) => asset.type.toLowerCase() === 'color image');
    const colorVideoAssets = this.colorDetails()?.assets.filter((asset) => asset.type.toLowerCase() === 'video');

    ambianceItems?.sort((a, b) => a.sequence - b.sequence);
    colorImageItems?.sort((a, b) => a.sequence - b.sequence);
    colorVideoAssets?.sort((a, b) => a.sequence - b.sequence);

    return [
      ...(ambianceItems ? this.#mapAssetsToGalleriaImages(ambianceItems) : []),
      ...(colorImageItems ? this.#mapAssetsToGalleriaImages(colorImageItems) : []),
      ...(colorVideoAssets ? this.#mapAssetsToGalleriaImages(colorVideoAssets, this.videoThumbnail()) : []),
    ];
  });

  constructor() {
    effect(() => {
      const colorDetails = this.colorDetails();
      if (colorDetails) {
        this.#colorGtmService.pushColorDetailsGTMTag(GoogleAnalyticsEvent.CdpDetailView, colorDetails);
        const meta = {
          title: colorDetails.metaTitle,
          description: colorDetails.metaDescription,
        } as PageLevelMeta;
        const pageLevelMetaTags = this.#pageLevelMetaService.getMetaTags(meta, MixedContentPageType.COLOR_DETAILS);
        this.#metaService.addTags(pageLevelMetaTags);
        if (colorDetails.metaTitle) {
          this.#titleService.setTitle(colorDetails.metaTitle);
        }
      }
    });

    this.#tintsAndShadesUrlUtilsService.initStripeIdQueryParamsUpdater({
      router: this.#router,
      relativeTo: this.#route,
      stripeId: computed(() => this.tintsAndShades()?.id || null),
      stripeIdFromQueryParams: toSignal(this.#queryParamsStripeId$, { initialValue: null }),
      enabled: this.tintsAndShadesEnabled,
    });
  }

  ngAfterViewInit() {
    this.headerContainers?.changes
      .pipe(
        startWith(this.headerContainers),
        map((event) => {
          const divContainer = [event.first, event.last].filter((div) => div?.nativeElement?.offsetHeight > 0)[0];
          return divContainer ? divContainer.nativeElement.offsetHeight + 'px' : '220px';
        }),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe((headerHeight) => this.#desktopHeaderHeight.set(headerHeight));

    this.#favoriteColorsService.favoriteColorAdded$
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((color) =>
        this.#colorGtmService.addColorToFavoritesGTMtag(color, GoogleAnalyticsPagePrefix.ColorDetailsPage),
      );

    this.#favoriteColorsService.favoriteColorRemoved$
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((color) =>
        this.#colorGtmService.removeColorFromFavoritesGTMtag(color, GoogleAnalyticsPagePrefix.ColorDetailsPage),
      );
  }

  onFavoriteButtonClicked(): void {
    const color = this.colorDetails();

    if (!color) {
      return;
    }

    if (this.isFavorite()) {
      this.#favoriteColorsService.removeFromFavorites(color);
    } else {
      this.#favoriteColorsService.addToFavorites(color);
    }
  }

  onColorWarrantyButtonClicked() {
    this.#colorGtmService.pushColorDetailsGTMTag(GoogleAnalyticsEvent.CdpWarrantyInfo, this.colorDetails());
  }

  onGalleriaItemChange(index: number) {
    this.#colorGtmService.changeGalleriaItemGTMTag(this.colorDetails(), index);
  }

  onColorFamilyClick() {
    this.#colorGtmService.pushColorDetailsGTMTag(GoogleAnalyticsEvent.CdpColorFamily, this.colorDetails());
  }

  onColorCollectionClick() {
    this.#colorGtmService.pushColorDetailsGTMTag(GoogleAnalyticsEvent.CdpColorCollection, this.colorDetails());
  }

  navigateToLaunchColorVisualizer(cta: CTA | undefined): void {
    this.#colorGtmService.pushColorDetailsGTMTag(GoogleAnalyticsEvent.CdpLaunchVisualizer, this.colorDetails());
    this.#urlCtaService.navigate(cta);
  }

  navigateToShowProducts(cta: CTA | null): void {
    if (!cta) {
      return;
    }
    this.#colorGtmService.pushColorDetailsGTMTag(
      GoogleAnalyticsEvent.CdpShowProducts,
      this.colorDetails(),
      GoogleAnalyticsCustomerJourney.Consideration,
    );
    this.#urlCtaService.navigate(cta);
  }

  navigateToFindAndBuy(cta: CTA | undefined, ctaIndex: number): void {
    switch (ctaIndex) {
      case this.colorConsultationFindAndBuyIndex:
        this.#colorGtmService.pushColorDetailsGTMTag(
          GoogleAnalyticsEvent.CdpColorConsultation,
          this.colorDetails(),
          GoogleAnalyticsCustomerJourney.Consideration,
        );
        break;
      case this.storeLocatorFindAndBuyIndex:
        this.#colorGtmService.pushColorDetailsGTMTag(
          GoogleAnalyticsEvent.CdpFindStore,
          this.colorDetails(),
          GoogleAnalyticsCustomerJourney.Consideration,
        );
        break;
    }
    this.#urlCtaService.navigate(cta);
  }

  private mapToColorSpecification(colorDetails: ColorInfoValid | null | undefined): ColorSpecification | null {
    if (!colorDetails) {
      return null;
    }
    return {
      colorFamilies: colorDetails.colorFamilies ?? [],
      colorCollectionNames: colorDetails.colorCollections ?? [],
      colorData: {
        rgb: colorDetails.rgb,
        lrv: colorDetails.lrv,
        ncs: colorDetails.ncs,
      } as ColorData,
    };
  }

  #buildBreadcrumbs(colorDetails: ColorInfoValid | null, content: Content): Breadcrumb[] {
    const colorName = colorDetails?.colorName;

    if (!colorName) {
      return [];
    }

    const colorFamiliesBreadcrumb = {
      title: content?.breadcrumbs[0]?.title,
      url: `${this.#appRoutesService.getPathByAppRouteFeatureModule(FeatureModule.ColorFamilies)}`,
      dataTestId: 'breadcrumbs.colorFamilies',
    };
    const colorNameBreadcrumb = {
      title: colorName,
      dataTestId: 'colorFamilies.selectedFamilyName',
    };
    return [colorFamiliesBreadcrumb, colorNameBreadcrumb];
  }

  #mapAssetsToGalleriaImages(assets: Asset[], videoThumbnail?: StaticAsset | undefined): GalleriaImage[] {
    return assets
      .filter((asset) => asset.url !== null)
      .map((asset) => ({
        id: asset.id,
        name: asset.name,
        type: asset.type == AssetType.Video ? GalleriaType.Video : GalleriaType.Image,
        src: asset.type == AssetType.Video ? asset.url! : (appendImageSize(asset.url!) ?? ''),
        thumbnailSrc:
          asset.type == AssetType.Video ? videoThumbnail?.url : (appendImageSize(asset.url!, 'SmallOptimized54') ?? ''),
        alt: asset.description ?? '',
      }));
  }

  getVideoThumbnail(): Observable<StaticAsset | undefined> {
    const siteId = this.#currentSiteState.siteId();
    const videoThumbnailCodename = this.videoThumbnailCodename();

    return this.#configService
      .getSiteStaticAssets(siteId, this.#languagesService.language())
      .pipe(map((assets) => assets?.items.find((asset) => asset.codename.includes(videoThumbnailCodename))));
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars,unused-imports/no-unused-vars
  startVideoClickHandler(item: GalleriaImage) {
    // GTM Event will be added here
  }

  #getCtaShowProducts(): CTA | null {
    const targetColor = this.colorDetails();

    if (!targetColor || !this.colorToProductJourneyEnabled()) {
      return null;
    }

    const cta = this.ctaShowProductsContent();

    if (!cta?.content?.url) {
      return null;
    }

    const stripeId = this.tintsAndShades()?.id;
    const rewrittenUrl = this.#featureRoutePathService.getColorToProductJourneyListPath({
      [COLOR_EMG_QUERY_PARAM]: targetColor.productNumber,
      ...(stripeId ? { [STRIPE_ID_QUERY_PARAM]: stripeId.toString() } : {}),
    });

    if (!rewrittenUrl) {
      return null;
    }

    return {
      ...cta,
      content: {
        ...cta.content,
        url: rewrittenUrl,
      },
    };
  }

  #getCoordinatingColors(targetColor: ColorInfoValid | null): Observable<Container603010Model | null> {
    if (!targetColor) {
      return of(null);
    }

    const algorithmsFromConfig = this.algorithmsFromConfig();

    return this.#coordinatingColorsService.getCoordinatingColors(targetColor).pipe(
      map((coordinatingColors) => {
        if (!coordinatingColors || !coordinatingColors.length) {
          return null;
        }

        const filteredAndOrderedCCs = algorithmsFromConfig
          .map((algorithm) => coordinatingColors.find((cc) => cc.algorithm === algorithm))
          .filter((cc): cc is CoordinatingColors => !!cc);

        return this.#mapCoordinatingColorsTo603010Model(filteredAndOrderedCCs);
      }),
    );
  }

  #mapCoordinatingColorsTo603010Model(coordinatingColors: CoordinatingColors[]): Container603010Model | null {
    const validComponents = coordinatingColors
      .filter((coordinatingColor) => coordinatingColor.secondary.isValid && coordinatingColor.accent.isValid)
      .map((coordinatingColor): Component603010Model => {
        return {
          headline: this.#resourceService.getResourceByKey(coordinatingColor.algorithm as ResourceItem),
          primaryColor: this.colorDetails()!,
          secondaryColor: coordinatingColor.secondary,
          accentColor: coordinatingColor.accent,
        };
      });

    if (!validComponents.length) {
      return null;
    }

    return {
      headline: this.#resourceService.getResourceByKey(ResourceItem.GLOBAL_COORDINATING_COLORS),
      components: validComponents,
      textAlignment: TextAlignment.CENTER,
    };
  }
}
